import { jsx as _jsx } from "react/jsx-runtime";
import { resolveBlockContract } from '../normalizeBlockTree.js';
import { resolveBlockRenderer } from '../registry.js';
import { toSafeRuntimeHtml } from '../security/safeInnerHtml.js';
import { fallbackMessage, renderFallback } from './blockTreeFallback.js';
import { asRecord, withBlockKey } from './blockTreeValues.js';
import { renderStructuralBlock } from './renderStructuralBlock.js';
const resolveRuntimeHtmlClassName = (value, block) => typeof value === 'function' ? value(block) : value;
export const renderBlockTree = (block, options) => {
    const structuralBlock = renderStructuralBlock(block, options, renderBlockTree);
    if (structuralBlock)
        return structuralBlock;
    const safeProps = asRecord(block.props);
    const resolution = resolveBlockRenderer(block.type, options.registry);
    const contract = resolveBlockContract(block);
    const runtimeMode = contract.runtimeMode ?? 'preview-only';
    const runtimeHtmlOptions = options.runtimeHtml;
    const runtimeHtml = runtimeHtmlOptions && typeof safeProps[runtimeHtmlOptions.htmlPropName] === 'string'
        ? safeProps[runtimeHtmlOptions.htmlPropName]
        : '';
    const rendererMatchesPlugin = Boolean(contract.pluginId) &&
        Boolean(resolution.definition?.pluginId) &&
        resolution.definition?.pluginId === contract.pluginId;
    const shouldPreferRuntimeHtml = Boolean(contract.pluginId) &&
        runtimeMode === 'storefront-runtime' &&
        runtimeHtml.trim().length > 0 &&
        !rendererMatchesPlugin;
    if (resolution.renderer && !shouldPreferRuntimeHtml) {
        const normalized = options.normalizeRendererProps?.(block) ?? { ok: true, props: safeProps };
        if (!normalized.ok) {
            return renderFallback({
                block,
                kind: 'invalid-config',
                message: fallbackMessage(block, normalized.message || `Invalid block config (${block.type})`),
                reason: 'invalid_config',
            }, options);
        }
        try {
            return withBlockKey(resolution.renderer(normalized.props, { block }), block);
        }
        catch (error) {
            options.onDiagnostic?.({ type: 'renderer-error', block, error });
            return renderFallback({
                block,
                kind: 'renderer-error',
                message: `Renderer failed for block "${block.type}"`,
                reason: 'renderer_throw',
                error,
            }, options);
        }
    }
    if (contract.pluginId && runtimeMode !== 'storefront-runtime') {
        return renderFallback({
            block,
            kind: 'plugin-runtime-disabled',
            message: `Plugin block unavailable (${block.type})`,
            reason: 'runtime_mode_not_storefront',
        }, options);
    }
    if (contract.pluginId && runtimeMode === 'storefront-runtime') {
        if (runtimeHtml.trim().length > 0 && runtimeHtmlOptions) {
            const safeRuntimeHtml = toSafeRuntimeHtml(runtimeHtml);
            const wrapperClassName = resolveRuntimeHtmlClassName(runtimeHtmlOptions.className, block) ??
                'rounded-xl border border-slate-200 bg-white p-3';
            const contentClassName = resolveRuntimeHtmlClassName(runtimeHtmlOptions.contentClassName, block);
            if (contentClassName) {
                return (_jsx("div", { className: wrapperClassName, children: _jsx("div", { className: contentClassName, dangerouslySetInnerHTML: safeRuntimeHtml }) }, block.id));
            }
            return (_jsx("div", { className: wrapperClassName, dangerouslySetInnerHTML: safeRuntimeHtml }, block.id));
        }
        const reason = runtimeHtmlOptions?.errorPropName && typeof safeProps[runtimeHtmlOptions.errorPropName] === 'string'
            ? String(safeProps[runtimeHtmlOptions.errorPropName])
            : 'runtime_html_missing';
        return renderFallback({
            block,
            kind: 'plugin-runtime-unavailable',
            message: `Plugin runtime unavailable (${block.type})`,
            reason,
        }, options);
    }
    return renderFallback({
        block,
        kind: 'missing-renderer',
        message: resolution.reason === 'plugin_renderer_missing'
            ? `Plugin renderer unavailable (${block.type})`
            : `Renderer missing for block "${block.type}"`,
        reason: resolution.reason,
    }, options);
};
